import { useEffect, useState } from "react";
import { copyShare, downloadCard, facebookShareUrl, instagramShare, shareImageAndLink } from "@/lib/resultSharing";

export function ShareActions({ text, url: destinationUrl, createCard, facebookCard, nativeShare = false, preparePublicLink = false, onPublicLink, filename = "HHKD-Results.png", copyLabel = "COPY", children }) {
  const [card, setCard] = useState(null);
  const [notice, setNotice] = useState("");
  const [showText, setShowText] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const [facebookLink, setFacebookLink] = useState("");
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [publicLink, setPublicLink] = useState("");
  const [linkError, setLinkError] = useState("");
  const url = publicLink || destinationUrl;
  useEffect(() => { setPublicLink(""); setLinkError(""); setFacebookLink(""); setNotice(""); setShowText(false); setShowSave(false); }, [destinationUrl]);
  useEffect(() => {
    let cancelled = false;
    setCard(null);
    createCard().then(blob => { if (!cancelled) setCard(blob); }).catch(() => {});
    return () => { cancelled = true; };
  }, [createCard]);
  useEffect(() => {
    if (!preparePublicLink) { setPublicLink(""); onPublicLink?.(""); return; }
    if (!preparePublicLink || !card || !facebookCard) return;
    let cancelled = false;
    facebookCard(card).then(link => { if (!cancelled) { setPublicLink(link); onPublicLink?.(link); } })
      .catch(() => { if (!cancelled) setLinkError("The preview link is unavailable. You can still save the image and copy the direct challenge link; Facebook can be retried."); });
    return () => { cancelled = true; };
  }, [preparePublicLink, card, facebookCard, onPublicLink]);
  const copy = async () => {
    try { await copyShare(text, url); setNotice("Results and link copied."); }
    catch { setShowText(true); setNotice("Select and copy the text below."); }
  };
  const facebook = async () => {
    if (!card) { setNotice("Your image is preparing. Try Facebook again in a moment."); return; }
    // Reserve a window inside the gesture, before the authenticated upload.
    const popup = window.open("", "_blank");
    if (popup) popup.opener = null;
    setFacebookBusy(true); setShowText(true); setNotice("Preparing your card preview…");
    try {
      const preview = publicLink || (facebookCard ? await facebookCard(card) : url);
      const link = facebookShareUrl(preview);
      setFacebookLink(link);
      if (popup && !popup.closed) popup.location.replace(link);
      try { await copyShare(text, url); } catch {}
      setNotice("Your card and results are available in the link preview. Paste them into your Facebook post if you want a caption. Facebook controls the preview; no post was sent. If the dialog did not open, use OPEN FACEBOOK below.");
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      setNotice(`Couldn't prepare the Facebook card. ${error.message}`);
    } finally { setFacebookBusy(false); }
  };
  const instagram = async () => {
    setShowSave(true); setShowText(true);
    setNotice("Choose Instagram in your phone’s share sheet if available. If it is missing, save the image and upload it in Instagram. Copy the caption/link separately; Instagram captions do not provide clickable web links.");
    try {
      const outcome = await instagramShare(card, filename, text, url);
      if (outcome === "downloaded") {
        setShowText(true);
        setNotice("Direct image sharing is unavailable here. Your card was downloaded. Open Instagram and upload it, then copy the caption below. No post was sent.");
      }
    } catch (error) {
      if (error.name === "AbortError") setNotice("Sharing cancelled. No post was sent.");
      else { setShowText(true); setNotice(error.message); }
    }
  };
  const native = async () => {
    setShowSave(true); setShowText(true);
    if (preparePublicLink && !publicLink && !linkError) { setNotice("Your invitation image and preview link are preparing. Try again in a moment."); return; }
    setNotice("Choose a messaging app to share the image and clickable challenge link. Some apps accept only the image; copy the link separately if needed.");
    try {
      const outcome = await shareImageAndLink(card, filename, text, url);
      setNotice(outcome === "shared" ? "Image and clickable challenge link offered to your chosen app. Some apps omit the message or attachment; use COPY CHALLENGE LINK if needed. No post was sent automatically." : "Image downloaded. Attach it to your message and use COPY CHALLENGE LINK for the clickable invitation.");
    } catch (error) { setNotice(error.name === "AbortError" ? "Sharing cancelled. No post was sent." : error.message); }
  };
  return <div className="hhkd-share" data-testid="share-actions">
    <div className="hhkd-share-buttons grid grid-cols-2 gap-3">
      <button type="button" disabled={facebookBusy} onClick={facebook} data-testid="share-facebook" className="border border-primary px-4 py-3 text-primary font-mono-caps text-xs">FACEBOOK</button>
      <button type="button" onClick={instagram} data-testid="share-instagram" className="border border-primary px-4 py-3 text-primary font-mono-caps text-xs">INSTAGRAM</button>
      <button type="button" disabled={preparePublicLink && !publicLink && !linkError} onClick={copy} data-testid="share-copy" className="border border-primary px-4 py-3 text-primary font-mono-caps text-xs">{copyLabel}</button>
      {children}
    </div>
    {preparePublicLink && !publicLink && <p role="status" className="mt-3 text-sm">{linkError || "Preparing your invitation image and preview link…"}</p>}
    {nativeShare && <button type="button" onClick={native} data-testid="share-native" className="mt-3 border border-primary px-4 py-3 text-primary text-xs">SHARE IMAGE & LINK</button>}
    {facebookLink && <a href={facebookLink} target="_blank" rel="noopener noreferrer" className="block mt-3 underline" data-testid="share-facebook-link">OPEN FACEBOOK</a>}
    {notice && <p role="status" className="mt-3 text-sm" data-testid="share-notice">{notice}</p>}
    {showSave && <><button type="button" disabled={!card} onClick={() => { downloadCard(card, filename); setNotice("Card saved. Attach or upload it in your chosen app. Copy the caption/link separately; Instagram captions do not provide clickable web links."); }} className="mt-3 border border-primary px-4 py-3 text-primary text-xs" data-testid="share-save-image">SAVE RESULTS IMAGE</button><button type="button" onClick={copy} className="mt-3 border border-primary px-4 py-3 text-primary text-xs" data-testid="share-copy-caption">COPY CAPTION & LINK</button></>}
    {showText && <label className="block mt-3 text-sm">Results and shareable link<textarea readOnly aria-label="Results and shareable link" value={`${text}\n${url}`} onFocus={e => e.target.select()} className="block w-full mt-2 p-3 bg-background text-foreground border border-white/30 text-xs" rows={4} /></label>}
  </div>;
}
